import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatUSD } from "@/lib/utils";
import { Network, ArrowRight, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

function Dot({ online, mode }) {
  if (mode === "disabled") return <span className="w-2 h-2 rounded-full bg-slate-400" />;
  return (
    <span
      className={"w-2 h-2 rounded-full " + (online ? "bg-emerald-500" : "bg-red-500")}
    />
  );
}

/**
 * Compact provider network snapshot for the Master Admin dashboard.
 * Pulls /api/admin/providers/dashboard and links to /provider for full detail.
 * Rendered only for MASTER_ADMIN — the parent already guards the role.
 *
 * Rate-limit safe (Sep 2026): the backend serves a 15-minute cached balance;
 * auto-poll below only re-reads the cache. The manual "Refresh" button calls
 * the per-provider healthcheck endpoint, which is the ONLY on-demand path
 * hitting the upstream provider APIs.
 */
export default function DashboardProviderStrip() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/providers/dashboard");
      setItems(data.items || []);
    } catch {
      /* silently ignore — MASTER_ADMIN only */
    } finally {
      setLoading(false);
    }
  };

  const refreshAll = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const enabled = items.filter((p) => p.enabled && p.mode !== "disabled");
      await Promise.allSettled(
        enabled.map((p) => api.post(`/admin/providers/${p.key}/healthcheck`))
      );
      await load();
      toast.success("Provider balances refreshed");
    } catch {
      toast.error("Balance refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // Cache-backed poll every 15 min — matches the server-side balance TTL.
    const t = setInterval(load, 900000);
    return () => clearInterval(t);
  }, []);

  if (loading || items.length === 0) return null;

  return (
    <div
      className="bg-white rounded-md border border-slate-200"
      data-testid="dashboard-provider-strip"
    >
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Network className="w-4 h-4 text-slate-500" />
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              Provider network
            </div>
            <div className="text-sm text-slate-700 mt-0.5">
              Live upstream carrier status
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={refreshAll}
            disabled={refreshing}
            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-50"
            data-testid="dashboard-provider-refresh-btn"
            title="Fetch fresh balances from providers (rate-limit safe)"
          >
            {refreshing ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <RefreshCw className="w-3 h-3" />
            )}
            Refresh
          </button>
          <Link
            to="/provider"
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#0F62FE] hover:underline"
            data-testid="dashboard-provider-details-link"
          >
            Manage <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-100">
        {items.map((p) => (
          <div
            key={p.key}
            className="p-4"
            data-testid={`dashboard-provider-${p.key}`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Dot online={p.online} mode={p.mode} />
                <span
                  className="text-sm font-bold text-slate-900"
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                >
                  {p.display_name}
                </span>
              </div>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                #{p.priority}
              </span>
            </div>
            <div className="mt-2 flex items-baseline justify-between">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                  Balance
                </div>
                <div
                  className="text-lg font-bold text-slate-900 flex items-center gap-1.5"
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                >
                  {p.balance != null ? formatUSD(p.balance) : "—"}
                  {p.balance != null && (
                    <span className="text-xs text-slate-500 ml-1">
                      {p.currency}
                    </span>
                  )}
                  {p.stale && (
                    <span
                      className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-widest bg-amber-50 text-amber-700 border border-amber-200"
                      data-testid={`dashboard-provider-stale-${p.key}`}
                      title="Last known value — upstream rate-limited. Refreshes automatically every 15 min."
                    >
                      cached
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                  Mode
                </div>
                <div
                  className={
                    "text-[10px] font-bold uppercase tracking-widest mt-0.5 " +
                    (p.mode === "live"
                      ? "text-emerald-600"
                      : p.mode === "mock"
                      ? "text-amber-600"
                      : "text-slate-400")
                  }
                >
                  {p.mode}
                </div>
              </div>
            </div>
            {p.health_detail && (
              <div className="mt-1 text-[10px] text-slate-500 truncate">
                {p.health_detail}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
